import { SendIcon } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import useText from 'hooks/useText';
import { useRef, useState } from 'react';
import MentionText from './MentionText';

const fullName = (person) => `${person.firstname} ${person.lastname}`.trim();

/**
 * The field that writes a comment: « @ » proposes the colleagues, whose names
 * take the colour of their avatar as they are typed; ⌘ or Ctrl + Entrée
 * sends. `onSend(body, mentions)` resolves true once the comment is saved.
 */
const CommentComposer = ({ people, meId, onSend }) => {
  const { text } = useText('comments');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const area = useRef(null);
  const mirror = useRef(null);
  const query = /(?:^|\s)@([^\s@]*)$/.exec(draft)?.[1];
  const suggestions =
    query === undefined
      ? []
      : people
          .filter((person) => person.id !== meId && fullName(person).toLowerCase().startsWith(query.toLowerCase()))
          .slice(0, 5);

  const pick = (person) => {
    setDraft((current) => current.replace(/@([^\s@]*)$/, `@${fullName(person)} `));
    area.current?.focus();
  };
  const send = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    const mentions = people.filter((person) => draft.includes(`@${fullName(person)}`)).map((person) => person.id);
    if (await onSend(draft.trim(), mentions)) setDraft('');
    setBusy(false);
  };

  return (
    <div className="relative flex flex-col gap-2 rounded-lg border border-gray-200 p-3 focus-within:border-yellow-500">
      {suggestions.length > 0 && (
        <div className="absolute bottom-full left-3 mb-1 w-60 rounded-lg border border-gray-100 bg-white py-1 shadow-lg">
          {suggestions.map((person) => (
            <button
              key={person.id}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                pick(person);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              <EmployeeAvatar employee={person} size={20} />
              {fullName(person)}
            </button>
          ))}
        </div>
      )}
      {/* The text is drawn in a layer under the field, which is transparent:
          the names it mentions take their colour as soon as they are typed. */}
      <div className="relative">
        <div
          ref={mirror}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-sm text-gray-900"
        >
          <MentionText text={draft} people={people} />
          {'\n'}
        </div>
        <textarea
          ref={area}
          rows={2}
          value={draft}
          aria-label={text('placeholder')}
          placeholder={text('placeholder')}
          onChange={(event) => setDraft(event.target.value)}
          onScroll={(event) => {
            if (mirror.current) mirror.current.scrollTop = event.target.scrollTop;
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              send();
            }
          }}
          className="relative block w-full resize-none bg-transparent text-sm text-transparent caret-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>
      <div className="flex justify-end">
        <Button color="SECONDARY" size="SM" disabled={!draft.trim() || busy} onClick={send}>
          <SendIcon wh={16} color="currentColor" />
          {text('submit')}
        </Button>
      </div>
    </div>
  );
};

export default CommentComposer;
